import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class CreatePromoService {
  /**
   * Applies a discount to rows that match the filter criteria within a full dataset.
   * Instead of filtering the data, it maps over the entire array and modifies matching rows.
   *
   * @param data The full array of table data.
   * @param filters The filter object from the dialog, including the 'Discount'.
   * @returns A new array containing all original items, with the discount applied to matching rows.
   */
  public applyDiscountToMatchingRows(
    data: any[],
    filters: { [key: string]: any },
  ): any[] {
    // If there are no filters, no discount, or no data, return the original data array without changes.
    if (
      !filters ||
      !data ||
      filters['Discount'] === undefined ||
      filters['Discount'] === null
    ) {
      return data;
    }

    const discount = filters['Discount'];
    const selectionFilters = { ...filters };
    delete selectionFilters['Discount'];
    delete selectionFilters['available_keys'];

    // Use map to return a new array with the same number of elements.
    return data.map((row) => {
      // Check if the current row matches the selection criteria.
      if (this.doesRowMatchFilters(row, selectionFilters)) {
        // If it matches, create a deep copy and apply the discount.
        const modifiedRow = JSON.parse(JSON.stringify(row));
        const discountMultiplier = 1 - discount / 100; // Corrected discount calculation

        if (modifiedRow.period_data) {
          // Iterate over each year in the period_data (e.g., "2024")
          for (const year in modifiedRow.period_data) {
            if (
              Object.prototype.hasOwnProperty.call(
                modifiedRow.period_data,
                year,
              )
            ) {
              // The value is an array of month objects
              modifiedRow.period_data[year].forEach((monthData: any) => {
                // Iterate over each month in the object (e.g., "01")
                for (const month in monthData) {
                  if (Object.prototype.hasOwnProperty.call(monthData, month)) {
                    const originalValue = monthData[month];
                    // Apply the discount and update the value
                    monthData[month] = originalValue * discountMultiplier;
                  }
                }
              });
            }
          }
        }
        return modifiedRow;
      } else {
        return row;
      }
    });
  }

  /**
   * Checks if a single row of data matches a given set of filters.
   * @param row The data row to check.
   * @param filters The selection filters to apply.
   * @returns True if the row matches all criteria, false otherwise.
   */
  private doesRowMatchFilters(
    row: any,
    filters: { [key: string]: any[] },
  ): boolean {
    const filterKeys = Object.keys(filters);

    // If there are no selection filters (e.g., only a discount is provided),
    // then every row is considered a match.
    if (filterKeys.length === 0) {
      return true;
    }

    // Check that the row's value is included in the array of selected values for every active filter.
    return filterKeys.every((key) => {
      const filterValues = filters[key];
      // If a filter key exists but has no selected values, it doesn't restrict the match.
      if (!filterValues || filterValues.length === 0) {
        return true;
      }
      return filterValues.includes(row[key]);
    });
  }
}
